// Capture for referral and promo codes carried in URL params (?ref=, ?promo=).
//
// Stored in localStorage scoped per publisher slug, with a 30-day TTL and
// last-touch-wins. Decoupled from React/forms so both the public landing
// page and the wizard can use the same primitives.
//
// Storage key shape: `selfservice:{kind}-code:{slug}` → JSON
//   { code: string, capturedAt: number /* ms epoch */ }
//
// TODO(code-capture): the cross-session/cross-buyer behavior here is rough —
// last-touch-wins, 30-day TTL, scoped per slug. Needs revisiting once the
// portal URL strategy is settled (vanity paths, subdomains per publisher,
// etc.). Open questions: should a code clear on submit so it doesn't carry
// to the next campaign? Should we use a short-lived cookie instead of
// localStorage so closing the browser ends attribution? How should we
// handle a buyer signing into a different account mid-flow? See feedback
// memory `feedback_no_remote_deploy_self_service` for related URL-deferral.

const TTL_MS = 30 * 24 * 60 * 60 * 1000;

type Kind = 'ref' | 'promo';

function key(kind: Kind, slug: string): string {
  return `selfservice:${kind}-code:${slug}`;
}

interface Stored {
  code: string;
  capturedAt: number;
}

export function readCapturedCode(kind: Kind, slug: string): string | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(key(kind, slug));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Stored;
    if (!parsed.code || typeof parsed.capturedAt !== 'number') {
      window.localStorage.removeItem(key(kind, slug));
      return null;
    }
    if (Date.now() - parsed.capturedAt > TTL_MS) {
      window.localStorage.removeItem(key(kind, slug));
      return null;
    }
    return parsed.code;
  } catch {
    window.localStorage.removeItem(key(kind, slug));
    return null;
  }
}

export function writeCapturedCode(kind: Kind, slug: string, code: string): void {
  if (typeof window === 'undefined') return;
  const v: Stored = { code: code.toUpperCase(), capturedAt: Date.now() };
  window.localStorage.setItem(key(kind, slug), JSON.stringify(v));
}

export function clearCapturedCode(kind: Kind, slug: string): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(key(kind, slug));
}

// Extract ?ref= and ?promo= from the current URL, write them to localStorage
// (last-touch wins), then strip them from the URL so a refresh doesn't
// re-trigger or leak the codes back into history. Idempotent — safe to call
// from any landing point.
export function captureCodesFromUrl(slug: string): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  let dirty = false;
  const ref = url.searchParams.get('ref');
  if (ref) {
    writeCapturedCode('ref', slug, ref);
    url.searchParams.delete('ref');
    dirty = true;
  }
  const promo = url.searchParams.get('promo');
  if (promo) {
    writeCapturedCode('promo', slug, promo);
    url.searchParams.delete('promo');
    dirty = true;
  }
  if (dirty) {
    // Replace history entry so the original ?ref= / ?promo= URL doesn't
    // appear in the back stack.
    window.history.replaceState({}, '', url.pathname + (url.search ? '?' + url.searchParams.toString() : '') + url.hash);
  }
}
